import React, { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../../../api/useFetchObjects";
import useDelete from "../../../../../api/useDelete";
import { useAuth } from "../../../../../context";
import DriverForm from "../DriverForm/DriverForm";

const DriversList = () => {
  const auth = useAuth();
  const token = auth?.user?.token;
  const { t } = useTranslation();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editDriver, setEditDriver] = useState(null);
  const [viewDriver, setViewDriver] = useState(null);

  const queryStr = [search ? `search=${search}` : "", statusFilter ? `status=${statusFilter}` : ""].filter(Boolean).join("&");
  const endpoint = queryStr ? `drivers/?${queryStr}` : "drivers/";

  const { data, isLoading, isError, refetch } = useFetchObjects(["drivers", search, statusFilter], endpoint, token);
  const { handleDelete, ConfirmDialog } = useDelete("drivers", token);

  const rows = Array.isArray(data) ? data : [];

  const handleOpenAdd = () => { setEditDriver(null); setFormOpen(true); };
  const handleOpenEdit = (driver) => { setEditDriver(driver); setFormOpen(true); };
  const handleFormClose = () => { setFormOpen(false); setEditDriver(null); refetch(); };

  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Grid container alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h4" color="primary" fontWeight={700}>
            {t("hr.drivers")}
          </Typography>
          <Button variant="contained" color="primary" startIcon={<AddCircleIcon />} onClick={handleOpenAdd}>
            {t("hr.addDriver")}
          </Button>
        </Grid>

        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              size="small"
              placeholder={t("hr.searchByDriver")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label={t("hr.status")} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">{t("common.all", "All")}</MenuItem>
              <MenuItem value="active">{t("hr.statusActive", "Active")}</MenuItem>
              <MenuItem value="inactive">{t("hr.statusInactive", "Inactive")}</MenuItem>
            </TextField>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2 }} />

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f2f2f2" }}>
                {[t("common.hash", "#"), t("hr.name"), t("hr.phone"), t("hr.email"), t("hr.licenseNo"), t("hr.expiryDate"), t("hr.status"), t("common.actions", "Actions")].map((h) => (
                  <TableCell key={h} align="center" sx={{ fontWeight: "bold" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody sx={{ bgcolor: "#ccfcfc" }}>
              {isLoading ? (
                <TableRow><TableCell colSpan={8} align="center">{t("common.loading", "Loading...")}</TableCell></TableRow>
              ) : isError ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ color: "red" }}>{t("hr.failedToLoadDrivers")}</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center">{t("hr.noDriversFound")}</TableCell></TableRow>
              ) : rows.map((d, i) => (
                <TableRow key={d.id} sx={{ backgroundColor: i % 2 === 0 ? "action.hover" : "background.default", "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell align="center">{i + 1}</TableCell>
                  <TableCell align="center">{d.name}</TableCell>
                  <TableCell align="center">{d.phone}</TableCell>
                  <TableCell align="center">{d.email || "—"}</TableCell>
                  <TableCell align="center">{d.license_number}</TableCell>
                  <TableCell align="center">{d.license_expiry_date}</TableCell>
                  <TableCell align="center">
                    <Chip label={d.status === "active" ? t("hr.statusActive", "Active") : t("hr.statusInactive", "Inactive")} color={d.status === "active" ? "success" : "default"} size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" color="info" onClick={() => setViewDriver(d)}><VisibilityIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="primary" onClick={() => handleOpenEdit(d)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(d.id)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={formOpen} onClose={handleFormClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {editDriver ? t("hr.editDriver") : t("hr.addNewDriver")}
          <IconButton onClick={handleFormClose}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <DriverForm driver={editDriver} onSuccess={handleFormClose} token={token} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewDriver} onClose={() => setViewDriver(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {t("hr.driverDetails")}
          <IconButton onClick={() => setViewDriver(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {viewDriver && (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <Avatar src={viewDriver.photo} sx={{ width: 80, height: 80, fontSize: 32 }}>
                  {viewDriver.name?.[0]}
                </Avatar>
              </Box>
              {[
                [t("hr.name"), viewDriver.name],
                [t("hr.phone"), viewDriver.phone],
                [t("hr.email"), viewDriver.email || "—"],
                [t("hr.address"), viewDriver.address || "—"],
                [t("hr.licenseNo"), viewDriver.license_number],
                [t("hr.expiryDate"), viewDriver.license_expiry_date],
                [t("hr.emergencyContact"), viewDriver.emergency_contact || "—"],
                [t("hr.experienceYears"), viewDriver.experience_years ?? "—"],
                [t("hr.status"), viewDriver.status === "active" ? t("hr.statusActive", "Active") : t("hr.statusInactive", "Inactive")],
                [t("hr.notes"), viewDriver.notes || "—"],
              ].map(([label, val]) => (
                <Grid container key={label} sx={{ py: 0.75, borderBottom: "1px solid #f0f0f0" }}>
                  <Grid item xs={5}><Typography fontWeight={600} variant="body2">{label}</Typography></Grid>
                  <Grid item xs={7}><Typography variant="body2">{val}</Typography></Grid>
                </Grid>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog />
    </Container>
  );
};

export default DriversList;
